import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, FileText } from "lucide-react";
import { http, formatDetail, auDateTime } from "./api";

const CLASSIFICATIONS = ["Public", "Internal", "Confidential", "Highly sensitive"];
const PI_OPTIONS = ["None", "Yes — de-identified", "Yes — identifiable"];
const YN = ["Yes", "No"];

const empty = {
  title: "", document_owner: "", source: "", document_date: "", classification: "",
  authority_to_provide: "", cultural_restrictions: "", personal_information_present: "",
  permitted_purpose: "", retention_requirements: "",
};
const emptyCK = {
  contains: "", nation_community: "", cultural_authority: "", permission_status: "",
  restrictions: "", intended_beneficiaries: "", may_be_quoted: "", may_appear_in_final_report: "",
  required_review_before_publication: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 block mb-1.5">{label}</label>
    {children}
  </div>
);

export default function EvidenceTab({ engagementId }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [meta, setMeta] = useState(empty);
  const [ck, setCk] = useState(emptyCK);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get(`/portal/client/engagements/${engagementId}/evidence`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load evidence."));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setMeta({ ...meta, [k]: e.target.value });
  const setC = (k) => (e) => setCk({ ...ck, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Attach a document.");
    if (!ck.contains) return toast.error("Answer the cultural knowledge question.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...meta, cultural_knowledge: ck }));
      fd.append("file", file);
      await http.post(`/portal/client/engagements/${engagementId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence submitted for Bigibila review.");
      setMeta(empty); setCk(emptyCK); setFile(null); setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const showCultural = ck.contains === "Yes" || ck.contains === "Unsure";
  const input = "field-dark w-full px-3 py-2.5 text-sm";

  return (
    <div data-testid="evidence-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif text-2xl">Evidence</h2>
        <button onClick={() => setShowForm(!showForm)} data-testid="toggle-evidence-form-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 flex items-center gap-2">
          <Upload size={14} /> {showForm ? "Close" : "Submit evidence"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-line bg-surface1 p-6 md:p-8 mb-8" data-testid="evidence-form">
          <p data-testid="evidence-warning" className="text-sm text-bone leading-relaxed border-l-2 border-ember pl-4 mb-8">
            Submit only information authorised for this engagement. Do not upload restricted cultural knowledge, health records, legal evidence, child-protection records or highly sensitive personal information unless Bigibila has established an appropriate secure handling process.
          </p>
          <div className="grid md:grid-cols-2 gap-5 mb-6">
            <Field label="Document title"><input data-testid="ev-title" className={input} value={meta.title} onChange={set("title")} /></Field>
            <Field label="Document owner"><input data-testid="ev-owner" className={input} value={meta.document_owner} onChange={set("document_owner")} /></Field>
            <Field label="Source"><input data-testid="ev-source" className={input} value={meta.source} onChange={set("source")} /></Field>
            <Field label="Date"><input type="date" data-testid="ev-date" className={input} value={meta.document_date} onChange={set("document_date")} /></Field>
            <Field label="Information classification">
              <select data-testid="ev-classification" className={input} value={meta.classification} onChange={set("classification")}>
                <option value="">Select…</option>{CLASSIFICATIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Personal information present">
              <select data-testid="ev-pi" className={input} value={meta.personal_information_present} onChange={set("personal_information_present")}>
                <option value="">Select…</option>{PI_OPTIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Authority to provide the document"><input data-testid="ev-authority" className={input} value={meta.authority_to_provide} onChange={set("authority_to_provide")} placeholder="e.g. CEO approval, board resolution" /></Field>
            <Field label="Cultural restrictions"><input data-testid="ev-restrictions" className={input} value={meta.cultural_restrictions} onChange={set("cultural_restrictions")} placeholder="e.g. None known" /></Field>
            <Field label="Permitted purpose"><input data-testid="ev-purpose" className={input} value={meta.permitted_purpose} onChange={set("permitted_purpose")} /></Field>
            <Field label="Retention or destruction requirements"><input data-testid="ev-retention" className={input} value={meta.retention_requirements} onChange={set("retention_requirements")} placeholder="e.g. Return on completion" /></Field>
          </div>

          <div className="border border-ember/40 bg-ink p-5 mb-6" data-testid="cultural-authority-block">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-3">Cultural authority</p>
            <Field label="Does this material contain or rely on First Nations cultural knowledge?">
              <select data-testid="ev-ck-contains" className={input} value={ck.contains} onChange={setC("contains")}>
                <option value="">Select…</option>{["No", "Yes", "Unsure"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            {showCultural && (
              <div className="grid md:grid-cols-2 gap-5 mt-5" data-testid="cultural-detail-fields">
                <Field label="Nation or community connected to the material"><input data-testid="ev-ck-nation" className={input} value={ck.nation_community} onChange={setC("nation_community")} /></Field>
                <Field label="Identified cultural authority"><input data-testid="ev-ck-authority" className={input} value={ck.cultural_authority} onChange={setC("cultural_authority")} /></Field>
                <Field label="Permission status"><input data-testid="ev-ck-permission" className={input} value={ck.permission_status} onChange={setC("permission_status")} placeholder="e.g. Granted, pending, unknown" /></Field>
                <Field label="Restrictions on access or use"><input data-testid="ev-ck-restrictions" className={input} value={ck.restrictions} onChange={setC("restrictions")} /></Field>
                <Field label="Intended beneficiaries"><input data-testid="ev-ck-beneficiaries" className={input} value={ck.intended_beneficiaries} onChange={setC("intended_beneficiaries")} /></Field>
                <Field label="May the material be quoted?">
                  <select data-testid="ev-ck-quoted" className={input} value={ck.may_be_quoted} onChange={setC("may_be_quoted")}><option value="">Select…</option>{YN.map((o) => <option key={o}>{o}</option>)}</select>
                </Field>
                <Field label="May it appear in a final report?">
                  <select data-testid="ev-ck-final" className={input} value={ck.may_appear_in_final_report} onChange={setC("may_appear_in_final_report")}><option value="">Select…</option>{YN.map((o) => <option key={o}>{o}</option>)}</select>
                </Field>
                <Field label="Required review before publication"><input data-testid="ev-ck-review" className={input} value={ck.required_review_before_publication} onChange={setC("required_review_before_publication")} placeholder="e.g. Elders' review required" /></Field>
              </div>
            )}
            <p className="font-mono text-[10px] text-stone2 mt-4">Possession of a document is not treated as proof of cultural authority.</p>
          </div>

          <Field label="Document (PDF, Word, Excel, PowerPoint, CSV, TXT or image — max 25 MB)">
            <input type="file" data-testid="ev-file" className="block text-sm text-stone2 file:mr-4 file:font-mono file:text-xs file:uppercase file:bg-ember file:text-ink file:border-0 file:px-4 file:py-2 file:cursor-pointer" onChange={(e) => setFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.png,.jpg,.jpeg" />
          </Field>
          <button type="submit" disabled={busy} data-testid="submit-evidence-btn" className="mt-6 font-mono text-sm uppercase tracking-[0.2em] px-8 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
            {busy ? "Uploading…" : "Submit for Bigibila review"}
          </button>
        </form>
      )}

      {items === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone2 border border-line bg-surface1 p-8" data-testid="evidence-empty">No evidence submitted yet.</p>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {items.map((it, i) => (
            <article key={it.id} data-testid={`evidence-row-${i}`} className="bg-ink p-5 md:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <FileText size={16} className="text-ember" />
                  <h3 className="font-serif text-lg">{it.title}</h3>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${it.status === "Accepted" ? "border-ember/50 text-ember" : it.status === "Clarification requested" ? "border-ochre text-ochre" : "border-line text-stone2"}`} data-testid={`evidence-status-${i}`}>{it.status}</span>
              </div>
              <div className="flex flex-wrap gap-2 font-mono text-[10px] text-stone2">
                <span className="px-2 py-0.5 border border-line">{it.original_filename}</span>
                <span className="px-2 py-0.5 border border-line">{it.classification}</span>
                <span className="px-2 py-0.5 border border-line">Cultural knowledge: {it.cultural_knowledge?.contains}</span>
                <span className="px-2 py-0.5 border border-line">{auDateTime(it.created_at)}</span>
              </div>
              {it.status === "Clarification requested" && it.clarification_note && (
                <p className="text-sm text-ochre mt-3 border-l-2 border-ochre pl-3" data-testid={`clarification-note-${i}`}>Bigibila requests: {it.clarification_note}</p>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
